<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { RouterLink } from 'vue-router';

import HeaderDateTime from './HeaderDateTime.vue';

/**
 * Header layout (3-column grid):
 *   • row 1 — logo (left), session player (center), tools (right)
 *   • row 2 — main nav (center column, same width as player)
 *   • row 3 — breadcrumbs
 *
 * Player width is synced to the main nav strip (see syncPlayerWidthToNav).
 */

const navRef = ref<HTMLElement | null>(null);
const playerRef = ref<HTMLElement | null>(null);

let resizeObserver: ResizeObserver | null = null;

function measureNavSingleLineWidth(navEl: HTMLElement): number {
  const prevWrap = navEl.style.flexWrap;
  const prevWidth = navEl.style.width;
  const prevMaxWidth = navEl.style.maxWidth;

  navEl.style.flexWrap = 'nowrap';
  navEl.style.width = 'max-content';
  navEl.style.maxWidth = 'none';

  const width = navEl.getBoundingClientRect().width;

  navEl.style.flexWrap = prevWrap;
  navEl.style.width = prevWidth;
  navEl.style.maxWidth = prevMaxWidth;

  return width;
}

function syncPlayerWidthToNav(): void {
  const player = playerRef.value;
  const nav = navRef.value;
  if (!player) return;

  if (window.matchMedia('(max-width: 800px)').matches) {
    player.style.maxWidth = '';
    return;
  }

  if (!nav) {
    player.style.maxWidth = '';
    return;
  }

  const navEl = nav.querySelector('.main-nav');
  if (!(navEl instanceof HTMLElement)) return;

  const navWidth = measureNavSingleLineWidth(navEl);
  if (navWidth > 0) {
    player.style.maxWidth = `${Math.ceil(navWidth)}px`;
  }
}

onMounted(() => {
  void nextTick(() => {
    syncPlayerWidthToNav();

    const observeTarget = navRef.value?.querySelector('.main-nav') ?? navRef.value;
    if (typeof ResizeObserver !== 'undefined' && observeTarget) {
      resizeObserver = new ResizeObserver(() => syncPlayerWidthToNav());
      resizeObserver.observe(observeTarget);
    }
  });

  window.addEventListener('resize', syncPlayerWidthToNav);
});

onUnmounted(() => {
  resizeObserver?.disconnect();
  window.removeEventListener('resize', syncPlayerWidthToNav);
});
</script>

<template>
  <header class="header">
    <div class="header__brand">
      <slot name="logo">
        <RouterLink
          class="header__logo"
          :to="{ name: 'home' }"
          aria-label="На главную"
        >
          <img
            class="header__logo-image header__logo-image--dark"
            src="/assets/swwhite.svg"
            alt=""
          />
          <img
            class="header__logo-image header__logo-image--light"
            src="/assets/swblack.svg"
            alt=""
          />
          <span
            class="header__logo-mark"
            aria-hidden="true"
          >TM</span>
        </RouterLink>
      </slot>
      <HeaderDateTime />
    </div>

    <div class="header__tools">
      <slot name="tools"></slot>
    </div>

    <div
      v-if="$slots.chamber || $slots.nav || $slots.employees"
      class="header__center"
    >
      <div
        v-if="$slots.chamber"
        ref="playerRef"
        class="header__player"
      >
        <slot name="chamber"></slot>
      </div>

      <div
        v-if="$slots.nav"
        ref="navRef"
        class="header__nav"
      >
        <slot name="nav"></slot>
      </div>

      <div
        v-if="$slots.employees"
        class="header__employees"
      >
        <slot name="employees"></slot>
      </div>
    </div>

    <div class="header__breadcrumbs">
      <slot name="breadcrumbs"></slot>
    </div>
  </header>
</template>
